import { supabase } from "./supabase";
import { settMittNavn, settSelskap } from "./konsoll";

/**
 * Oppstarten: å opprette en produksjon.
 *
 * ========  ÉN FUNKSJON I BASEN, IKKE TRE INNSETTINGER  ========
 *
 * Flata kaller `opprett_produksjon` og skriver ingenting selv. Grunnen
 * står i migrasjon 20261029140000: et ferskt selskap har ingen medlemmer,
 * så `tilgang.er_i_selskap` er usann i det sekundet selskapet lages, og en
 * policy som skulle sluppet det gjennom måtte spurt «er selskapet tomt?» —
 * et spørsmål som åpner et hull, fordi underspørringen leser med
 * spørrerens egne rettigheter.
 *
 * Funksjonen gjør selskap, produksjon og medlemskap i én transaksjon. Det
 * er ikke mulig å ende opp med et halvt oppsett.
 *
 * ========  LOGOENE KOMMER ETTERPÅ  ========
 *
 * De lastes opp under Oppsett·Produksjon, ikke her. En logo må ligge på
 * `produksjon/<id>/…`, og den id-en finnes ikke før produksjonen gjør det.
 */

export type Lederrolle = "produsent" | "prodleder";

export interface NyProduksjon {
  readonly produksjonsnavn: string;
  /** Foerste opptaksdag. Rammer kalenderen; selve dagene er `shoot_day`. */
  readonly startdato: string;
  readonly sluttdato: string;
  readonly rolle: Lederrolle;
  /**
   * Settes paa kontoen foerst, naar den mangler dem.
   *
   * Tomme strenger betyr «kontoen har dem alt». Dette sto som en
   * FEILMELDING paa siste steg — «Sett opp selskapet under Kontoen din
   * foerst» — etter at alt annet var fylt ut. En blindvei paa slutten av
   * et skjema er den verste plassen aa ha en.
   */
  readonly selskapsnavn: string;
  readonly mittnavn: string;
}

export type Svar =
  | { readonly ok: true; readonly produksjonId: string }
  | { readonly ok: false; readonly feil: string };

export async function opprettProduksjon(ny: NyProduksjon): Promise<Svar> {
  if (supabase === null) return { ok: false, feil: "Ingen forbindelse." };

  /* Kontoen foerst. Funksjonen i basen krever begge deler, og den som
     fyller ut skjemaet skal ikke sendes ut for aa hente dem. */
  if (ny.selskapsnavn.trim() !== "") {
    const svar = await settSelskap(ny.selskapsnavn);
    if (!svar.ok) return { ok: false, feil: svar.feil ?? "Selskapet ble ikke lagret." };
  }
  if (ny.mittnavn.trim() !== "") {
    const svar = await settMittNavn(ny.mittnavn);
    if (!svar.ok) return { ok: false, feil: svar.feil ?? "Navnet ble ikke lagret." };
  }

  const { data, error } = await supabase.rpc("opprett_produksjon", {
    p_produksjonsnavn: ny.produksjonsnavn.trim(),
    p_rolle: ny.rolle,
    p_startdato: ny.startdato === "" ? null : ny.startdato,
    p_sluttdato: ny.sluttdato,
  });

  /* Funksjonens egne `raise exception` kommer hit som `message`. De er
     skrevet for aa kunne vises rett fram — se migrasjonen. */
  if (error !== null) return { ok: false, feil: error.message };
  if (typeof data !== "string") {
    return { ok: false, feil: "Produksjonen ble ikke opprettet." };
  }
  return { ok: true, produksjonId: data };
}
